import React, { useState, useEffect, useRef } from 'react';
import { Arrow } from '@/components/Hero';

const CONTACT_ENDPOINT = 'https://fstldcxemuvmpwgxfqsp.supabase.co/functions/v1/contact-submit';
// Keep in sync with CONSENT_TEXT in the contact-submit edge function, which stores it with each submission.
const CONSENT_TEXT = 'I agree to be contacted by a Rapid Rune representative by phone or email about my inquiry.';
const FALLBACK_ERROR ='The message did not send. Check your connection and try again, or email inquire@rapidrune.com.';


const Contact = () => {
  const [status, setStatus] = useState('idle');
  const [error, setError] = useState('');
  const turnstileRef = useRef(null);
  const widgetIdRef = useRef(null);

  useEffect(() => {
    const renderWidget = () => {
      if (turnstileRef.current && widgetIdRef.current === null && window.turnstile) {
        widgetIdRef.current = window.turnstile.render(turnstileRef.current, {
          sitekey: '0x4AAAAAADVRhGpa1rU1WS0R',
          theme: 'dark',
        });
      }
    };

    let poll;
    if (window.turnstile) {
      renderWidget();
    } else {
      poll = setInterval(() => {
        if (window.turnstile) {
          clearInterval(poll);
          renderWidget();
        }
      }, 100);
    }

    return () => {
      clearInterval(poll);
      if (widgetIdRef.current !== null && window.turnstile) {
        window.turnstile.remove(widgetIdRef.current);
        widgetIdRef.current = null;
      }
    };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    const token = e.target.querySelector('[name="cf-turnstile-response"]')?.value;
    if (!token) {
      setError('Complete the security check above, then send again.');
      return;
    }

    setStatus('sending');
    const formData = Object.fromEntries(new FormData(e.target).entries());

    try {
      const res = await fetch(CONTACT_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...formData, 'cf-turnstile-response': token }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.ok) throw new Error(data.error || FALLBACK_ERROR);
      setStatus('sent');
    } catch (err) {
      setStatus('idle');
      setError(err instanceof TypeError ? FALLBACK_ERROR : err.message);
      // Turnstile tokens are single-use; get a fresh one for the retry.
      if (widgetIdRef.current !== null && window.turnstile) window.turnstile.reset(widgetIdRef.current);
    }
  };

  return (
    <section id="contact" className="contact" data-testid="contact-section">
      <div className="wrap contact-inner">
        <div className="contact-head">
          <h2 className="reveal">Let's solve your biggest bottleneck.</h2>
          <p className="reveal reveal-1">
            Tell us what's slowing your team down. We'll respond within one business day with a plan of action.
          </p>
          <ul className="contact-meta reveal reveal-2" data-testid="contact-info-panel">
            <li><a href="mailto:inquire@rapidrune.com">inquire@rapidrune.com</a></li>
            <li>Serving clients nationwide</li>
            <li>Reply within one business day</li>
          </ul>
        </div>

        {status === 'sent' ? (
          <div className="contact-sent" role="status" data-testid="contact-sent">
            <h3>Message received.</h3>
            <p>We'll reply to the email you gave us within one business day.</p>
          </div>
        ) : (
          <form className="contact-form reveal reveal-2" onSubmit={handleSubmit} data-testid="contact-form">
            <div className="form-head" aria-hidden="true">
              <span>Project request</span>
              <span>Reply within one business day</span>
            </div>
            <label className="cell">
              <span>First name</span>
              <input type="text" name="first_name" autoComplete="given-name" required data-testid="input-first-name" />
            </label>
            <label className="cell">
              <span>Last name</span>
              <input type="text" name="last_name" autoComplete="family-name" required data-testid="input-last-name" />
            </label>
            <label className="cell">
              <span>Work email</span>
              <input type="email" name="email" autoComplete="email" required data-testid="input-email" />
            </label>
            <label className="cell">
              <span>Phone</span>
              <input
                type="tel"
                name="phone"
                autoComplete="tel"
                inputMode="tel"
                required
                pattern="[0-9+()\-.\s]{7,25}"
                title="Enter a phone number, for example (555) 555-0123"
                data-testid="input-phone"
              />
            </label>
            <label className="cell">
              <span>Company</span>
              <input type="text" name="company" autoComplete="organization" data-testid="input-company" />
            </label>
            <label className="cell">
              <span>Primary interest</span>
              <select name="service" defaultValue="" data-testid="select-service">
                <option value="">Choose a service</option>
                <option>Process Automation</option>
                <option>IT Consulting &amp; Email Security</option>
                <option>M365 / Google Workspace Optimization</option>
                <option>Marketing Automation</option>
                <option>Multiple / Not Sure Yet</option>
              </select>
            </label>
            <label className="cell cell-full">
              <span>What workflow or problem should we solve?</span>
              <textarea name="message" rows="4" required data-testid="textarea-message" />
            </label>
            {/* Honeypot: hidden from people and screen readers; bots that fill it are dropped. */}
            <input
              type="text"
              name="website"
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
              style={{ position: 'absolute', left: '-9999px', width: '1px', height: '1px', opacity: 0 }}
            />
            <div className="contact-submit-row">
              <div className="contact-turnstile" ref={turnstileRef} />
              <div className="contact-submit-side">
                <label className="contact-consent">
                  <input type="checkbox" name="consent" value="yes" required data-testid="checkbox-consent" />
                  <span>
                    {CONSENT_TEXT} See our <a href="/privacy-policy">privacy policy</a>.
                  </span>
                </label>
                {error && <p className="contact-error" role="alert">{error}</p>}
                <button type="submit" className="contact-submit" disabled={status === 'sending'} data-testid="form-submit-button">
                  {status === 'sending' ? 'Sending' : 'Send message'}
                  <Arrow />
                </button>
              </div>
            </div>
          </form>
        )}
      </div>
    </section>
  );
};

export default Contact;
